import { isApiError } from './api/errors';

/*
  Merging locally detected field errors with the server's.

  Both exist and both matter. Local validation catches the empty required field without a round
  trip, and the server catches everything only it can know: a slug already taken, a site name
  already used in that organization, a parent that moved since the form loaded. A form that
  only did one of the two either round-trips on a blank field or silently drops a 409.

  The precedence is local first. If the technician has just cleared a field, the message about
  that should be the empty-field message, not the "already taken" message from the submission
  before it.

  Note that these return `string | undefined` rather than `string | null`, because that is what
  Field and Textarea take: `error` is an optional prop, and passing undefined is what "no error"
  means to them.
*/

export type LocalFieldErrors = Readonly<Record<string, string>>;

export interface FieldErrorReader {
  /** The message to show under a named field, or undefined when it is fine. */
  readonly forField: (name: string) => string | undefined;
  /** True when the failure was a 422 whose messages are all attached to named fields. */
  readonly allErrorsAreFieldLevel: boolean;
}

export function mergeFieldErrors(local: LocalFieldErrors, error: unknown): FieldErrorReader {
  const server = isApiError(error) ? error.fieldErrors : {};
  const serverKeys = Object.keys(server);

  return {
    forField: (name) => local[name] ?? server[name]?.[0],
    // Used to decide whether a summary alert is also needed. A 422 whose every message is
    // under a field is already fully explained next to the fields; a 409 or a 403 is not, and
    // showing nothing in that case is the silent failure this rule exists to prevent.
    allErrorsAreFieldLevel:
      isApiError(error) && error.kind === 'validation' && serverKeys.length > 0,
  };
}

/** Trimmed value, or null. For the many nullable text columns in the schema. */
export function trimToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
